import { Helmet } from 'react-helmet-async';
import { useSiteData } from '@/hooks/useSiteData';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { loadNOTAMData } from '@/utils/yamlLoader';
import type { NOTAM } from '@/data/types';

export function NOTAMs() {
  const { siteData } = useSiteData();
  const { notams } = loadNOTAMData();

  const activeNOTAMs = notams.filter(notam => {
    if (notam.status !== 'active') return false;
    const now = new Date();
    const effectiveDate = new Date(notam.effectiveDate);
    const expirationDate = new Date(notam.expirationDate);
    return now >= effectiveDate && now <= expirationDate;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/New_York'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/New_York',
      timeZoneName: 'short'
    });
  };

  return (
    <>
      <Helmet>
        <title>NOTAMs - {siteData.title}</title>
        <meta name="description" content="Notice to Airmen (NOTAMs) for Minute Man Air Field - Current operational notices and runway closures" />
        <meta name="keywords" content="NOTAM, aviation notices, runway closure, airfield operations, pilot information" />

        {/* Open Graph tags */}
        <meta property="og:title" content={`NOTAMs - ${siteData.title}`} />
        <meta property="og:description" content="Notice to Airmen (NOTAMs) for Minute Man Air Field - Current operational notices and runway closures" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.minutemanairfield.com/notams" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`NOTAMs - ${siteData.title}`} />
        <meta name="twitter:description" content="Notice to Airmen (NOTAMs) for Minute Man Air Field - Current operational notices and runway closures" />

        {/* Additional SEO */}
        <link rel="canonical" href="https://www.minutemanairfield.com/notams" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Minute Man Air Field" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Notice to Airmen (NOTAMs)</h1>
            <p className="text-lg text-gray-600">
              Current operational notices and important information for pilots flying into Minute Man Air Field.
            </p>
          </div>

          {activeNOTAMs.length > 0 ? (
            <div className="space-y-6">
              {activeNOTAMs.map((notam: NOTAM, index: number) => (
                <div key={`notam-${index}`} className="bg-red-50 border-l-4 border-red-400 p-6 shadow-sm rounded-r-md">
                  <div className="mb-4">
                    <div className="mb-2">
                      <h2 className="text-xl font-semibold text-red-800">{notam.title}</h2>
                    </div>
                    <div className="text-sm text-red-600 space-y-1">
                      <div><strong>Posted:</strong> {formatDate(notam.postedDate)}</div>
                      <div><strong>Runway:</strong> {notam.runway} ({notam.runwayDirection})</div>
                      <div><strong>Effective:</strong> {formatDateTime(notam.effectiveDate)}</div>
                      <div><strong>Expires:</strong> {formatDateTime(notam.expirationDate)}</div>
                    </div>
                  </div>

                  <div className="text-gray-900 leading-relaxed">
                    <p>{notam.description}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-2xl mx-auto">
                <h2 className="text-lg font-medium text-green-800 mb-2">No Current NOTAMs</h2>
                <p className="text-green-600">
                  There are currently no active NOTAMs for Minute Man Air Field. All runways and facilities are operating normally.
                </p>
              </div>
            </div>
          )}

          <div className="mt-12 space-y-8">
            {/* Official NOTAM Search */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-yellow-800 mb-3">Official NOTAM Search</h3>
              <p className="text-yellow-700 mb-4">
                For comprehensive and official NOTAM information, including system-wide notices and detailed aviation alerts,
                please use the FAA's official NOTAM search system.
              </p>
              <a
                href="https://notams.aim.faa.gov/notamSearch/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-yellow-600 hover:bg-yellow-700 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Access Official FAA NOTAM Search
              </a>
            </div>

            {/* Important Information */}
            <div className="border-t border-gray-200 pt-8">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-blue-800 mb-3">Important Information</h3>
                <div className="text-blue-700 space-y-2 text-sm">
                  <p>• NOTAMs are updated regularly. Please check back frequently for the latest information.</p>
                  <p>• All times are displayed in Eastern Time (ET).</p>
                  <p>• For urgent operational questions, contact the airport directly.</p>
                  <p>• Pilots should also check official NOTAM sources and weather briefings before flight.</p>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}